import { toast } from "react-toastify";
import { useAIProcessing } from "../../../Utils/AIProcessingOverlay";
import { useContext, useEffect, useState, useRef } from "react";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ArticleIcon from "@mui/icons-material/Article";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import { postData } from "../../../Services/postData";
import { urlEnum } from "../../../Utils/urlEnum";
import { VehicleContext } from "../../../Context/vehicleContext";
import { CacheContext } from "../../../Context/cacheContext";
import { disableTostifyConfig, findByName } from "../../../Utils/utils";
import { Vocabulary } from "../../../Utils/Vocabulary";
import { StepActions } from "../StepActions";

interface VehiclePhotoStepProps {
  onNext: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const methodCardStyle = (isDragOver: boolean) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  p: { xs: 2, sm: 3 },
  border: "2px dashed",
  borderColor: isDragOver ? "#FF6633" : "rgba(255, 102, 51, 0.35)",
  borderRadius: 2,
  cursor: "pointer",
  transition: "all 0.2s",
  bgcolor: isDragOver ? "rgba(255, 102, 51, 0.12)" : "rgba(255, 102, 51, 0.04)",
  transform: isDragOver ? "scale(1.02)" : "scale(1)",
  "&:hover": { borderColor: "#FF6633", bgcolor: "rgba(255, 102, 51, 0.10)" },
});

const fileCardStyle = {
  flex: 1,
  position: "relative" as const,
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  p: { xs: 2, sm: 3 },
  borderRadius: 2,
  bgcolor: "#f5f5f5",
  minHeight: 120,
};

const addVersoCardStyle = (isDragOver: boolean) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  p: { xs: 2, sm: 3 },
  border: "2px dashed",
  borderColor: isDragOver ? "#FF6633" : "rgba(255, 102, 51, 0.35)",
  borderRadius: 2,
  cursor: "pointer",
  transition: "all 0.2s",
  bgcolor: isDragOver ? "rgba(255, 102, 51, 0.12)" : "transparent",
  "&:hover": { borderColor: "#FF6633", bgcolor: "rgba(255, 102, 51, 0.04)" },
});

const VehiclePhotoStep: React.FC<VehiclePhotoStepProps> = ({
  onNext,
  onPrevious,
  isFirstStep = false,
  isLastStep = false,
}) => {
  const { startProcessing, stopProcessing } = useAIProcessing();
  const vehicleContext = useContext(VehicleContext) as any;
  const cacheContext = useContext(CacheContext);

  const currentVehicle =
    vehicleContext.state?.vehicle || vehicleContext.vehicle;

  const photoInputRef = useRef<HTMLInputElement>(null);
  const briefFrontInputRef = useRef<HTMLInputElement>(null);
  const briefBackInputRef = useRef<HTMLInputElement>(null);
  const hasInitialized = useRef(false);
  // Ref keeps the front file accessible without stale closure issues
  const briefFrontRef = useRef<File | null>(null);

  const [photo, setPhoto] = useState<{
    file: File | null;
    previewUrl: string | null;
  }>({
    file: null,
    previewUrl: null,
  });
  const [briefFront, setBriefFront] = useState<File | null>(null);
  const [briefBack, setBriefBack] = useState<File | null>(null);
  const [detected, setDetected] = useState(false);
  const [dragOver, setDragOver] = useState<"photo" | "brief" | "verso" | null>(
    null,
  );

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;

      // PRIORITY: restore brief state
      if (currentVehicle?.brief_front_file) {
        const front = currentVehicle.brief_front_file;
        const back = currentVehicle?.brief_back_file || null;

        briefFrontRef.current = front;
        setBriefFront(front);
        setBriefBack(back);
        setDetected(true);

        // optional: also set preview image (for consistency with detection flow)
        const imageFile = front.type?.startsWith("image/")
          ? front
          : back?.type?.startsWith("image/")
            ? back
            : null;

        if (imageFile) {
          const previewUrl = URL.createObjectURL(imageFile);
          setPhoto({ file: imageFile, previewUrl });
        }

        return;
      }

      // fallback: restore photo
      if (currentVehicle?.main_image_file) {
        const file = currentVehicle.main_image_file;
        setPhoto({ file, previewUrl: URL.createObjectURL(file) });
        setDetected(true);
      } else if (currentVehicle?.main_image) {
        setPhoto({ file: null, previewUrl: currentVehicle.main_image });
        setDetected(true);
      }
    }
  }, [
    currentVehicle?.brief_front_file,
    currentVehicle?.brief_back_file,
    currentVehicle?.main_image,
    currentVehicle?.main_image_file,
  ]);

  useEffect(() => {
    return () => {
      if (photo.previewUrl?.startsWith("blob:"))
        URL.revokeObjectURL(photo.previewUrl);
    };
  }, []);

  const parseYear = (year: string | number | undefined): number | undefined => {
    if (year === undefined || year === null) return undefined;
    if (typeof year === "number") return year;
    const match = String(year).match(/\d{4}/);
    return match ? parseInt(match[0], 10) : undefined;
  };

  const applyDetectedData = (
    detectedData: any,
    previewUrl: string,
    file: File,
  ) => {
    const brandObject = findByName(
      cacheContext.cache?.brands,
      detectedData.brand,
    );
    const modelObject = findByName(
      cacheContext.cache?.models,
      detectedData.model,
      (m) => (m as any).brand_id === (brandObject as any)?.id,
    );
    vehicleContext.updateVehicle({
      ...currentVehicle,
      vin: detectedData.vin || currentVehicle?.vin,
      brand_r: brandObject || currentVehicle?.brand_r,
      model_r: modelObject || currentVehicle?.model_r,
      variant: detectedData.variant || currentVehicle?.variant,
      an_fabricatie:
        parseYear(detectedData.year) || currentVehicle?.an_fabricatie,
      culoare_r: findByName(cacheContext.cache?.colors, detectedData.color),
      capacitate_cilindrica:
        detectedData.engineCapacity || currentVehicle?.capacitate_cilindrica,
      putere_cp: detectedData.power || currentVehicle?.putere_cp,
      tip_caroserie_r:
        findByName(cacheContext.cache?.bodyworkTypes, detectedData.bodyType) ||
        currentVehicle?.tip_caroserie_r,
      norma_poluare_r: findByName(
        cacheContext.cache?.pollutionNorms,
        detectedData.emissionStandard,
      ),
      tip_vehicul_r: cacheContext.cache?.vehicleTypes?.[0],
      optiune_culoare_r: cacheContext.cache?.colorsOptions?.[0],
      combustibil_r: findByName(
        cacheContext.cache?.fuelTypes,
        detectedData.fuelType,
      ),
      cutie_viteze_r: findByName(
        cacheContext.cache?.gearboxes,
        detectedData.transmission,
      ),
      tip_tractiune_r: cacheContext.cache?.tractionTypes?.[0],
      main_image: previewUrl,
      main_image_file: file,
      brief_front_file:
        detectedData.brief_front_file ?? currentVehicle?.brief_front_file,
      brief_back_file:
        detectedData.brief_back_file ?? currentVehicle?.brief_back_file,
      dotari: detectedData.features?.length
        ? detectedData.features
            .map((id: number) =>
              cacheContext.cache?.facilities?.find((f: any) => f.id === id),
            )
            .filter(Boolean)
        : currentVehicle?.dotari || [],
    });
  };

  const handlePhotoUpload = async (files: FileList | null) => {
    if (!files?.length) return;
    if (photo.previewUrl?.startsWith("blob:"))
      URL.revokeObjectURL(photo.previewUrl);
    const file = files[0];
    const previewUrl = URL.createObjectURL(file);
    setPhoto({ file, previewUrl });
    startProcessing(Vocabulary.processingVehicleData);
    try {
      const formData = new FormData();
      formData.append("carPhoto", file);
      const response = await postData(
        urlEnum.scanVehiclePhoto + disableTostifyConfig,
        formData,
      );
      const detectedData = response.data?.data;
      if (detectedData) {
        setDetected(true);
        applyDetectedData(detectedData, previewUrl, file);
        onNext();
      }
    } catch {
      toast.warn(Vocabulary.couldNotDetectVehicle);
    } finally {
      stopProcessing();
    }
  };

  const handleBriefFrontSelect = async (files: FileList | null) => {
    if (!files?.length) return;
    const front = files[0];
    briefFrontRef.current = front;
    setBriefFront(front);
    await processBrief(front, null);
  };

  const handleBriefBackSelect = async (files: FileList | null) => {
    if (!files?.length) return;
    const front = briefFrontRef.current;
    if (!front) return;
    const back = files[0];
    setBriefBack(back);
    await processBrief(front, back);
  };

  const processBrief = async (front: File, back: File | null) => {
    startProcessing(Vocabulary.processingVehicleData);
    try {
      const formData = new FormData();
      formData.append("vehicleDocument[]", front);
      if (back) formData.append("vehicleDocument[]", back);
      const response = await postData(
        urlEnum.scanVehicleBrief + disableTostifyConfig,
        formData,
      );
      const detectedData = response.data?.data;
      if (detectedData) {
        setDetected(true);
        const imageFile = front.type.startsWith("image/") ? front : back;
        const previewUrl = imageFile ? URL.createObjectURL(imageFile) : "";
        // Store brief files in context so they can be uploaded as documents after vehicle creation
        applyDetectedData(
          {
            ...detectedData,
            brief_front_file: front,
            brief_back_file: back ?? undefined,
          },
          previewUrl,
          front,
        );
      }
    } catch {
      toast.warn(Vocabulary.couldNotDetectVehicle);
    } finally {
      stopProcessing();
    }
  };

  const resetBrief = () => {
    briefFrontRef.current = null;
    setBriefFront(null);
    setBriefBack(null);
    setDetected(false);
    vehicleContext.updateVehicle?.({
      ...currentVehicle,
      brief_front_file: null,
      brief_back_file: null,
      main_image: null,
      main_image_file: null,
    });
  };

  const removePhoto = () => {
    if (photo.previewUrl?.startsWith("blob:"))
      URL.revokeObjectURL(photo.previewUrl);
    setPhoto({ file: null, previewUrl: null });
    setDetected(false);
    vehicleContext.updateVehicle?.({
      ...currentVehicle,
      main_image: null,
      main_image_file: null,
    });
  };

  const hasBrief = !!briefFront;
  const hasUploadedContent = photo.previewUrl || hasBrief;

  const activateOnEnterOrSpace =
    (activate: () => void) => (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        activate();
      }
    };

  return (
    <Box sx={{ width: "100%" }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <CameraAltIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="body1" fontWeight={600}>
          {Vocabulary.vehicleDetection}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {Vocabulary.chooseDetectionMethod}
      </Typography>

      {!hasUploadedContent ? (
        /* Method selection */
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
          <Box
            role="button"
            tabIndex={0}
            aria-label={Vocabulary.vehiclePhotoMethod}
            sx={methodCardStyle(dragOver === "photo")}
            onClick={() => photoInputRef.current?.click()}
            onKeyDown={activateOnEnterOrSpace(() => photoInputRef.current?.click())}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver("photo");
            }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(null);
              handlePhotoUpload(e.dataTransfer.files);
            }}
          >
            <CameraAltIcon sx={{ fontSize: 40, color: "#FF6633" }} />
            <Typography variant="subtitle2" fontWeight={700} textAlign="center">
              {Vocabulary.vehiclePhotoMethod}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              {Vocabulary.detectFromImage}
            </Typography>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handlePhotoUpload(e.target.files)}
              style={{ display: "none" }}
            />
          </Box>

          <Box
            role="button"
            tabIndex={0}
            aria-label={Vocabulary.vehicleBookBrief}
            sx={methodCardStyle(dragOver === "brief")}
            onClick={() => briefFrontInputRef.current?.click()}
            onKeyDown={activateOnEnterOrSpace(() => briefFrontInputRef.current?.click())}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver("brief");
            }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(null);
              handleBriefFrontSelect(e.dataTransfer.files);
            }}
          >
            <ArticleIcon sx={{ fontSize: 40, color: "#FF6633" }} />
            <Typography variant="subtitle2" fontWeight={700} textAlign="center">
              {Vocabulary.vehicleBookBrief}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              {Vocabulary.frontAndBackMaxFiles}
            </Typography>
            <input
              ref={briefFrontInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleBriefFrontSelect(e.target.files)}
              style={{ display: "none" }}
            />
          </Box>
        </Box>
      ) : hasBrief ? (
        /* Brief upload state — two side-by-side cards */
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
          {/* Front file card */}
          <Box sx={fileCardStyle}>
            <IconButton
              size="small"
              onClick={resetBrief}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                bgcolor: "rgba(0,0,0,0.15)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.3)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <ArticleIcon sx={{ fontSize: 36, color: "#FF6633" }} />
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                px: 1,
              }}
            >
              {briefFront!.name}
            </Typography>
            <Chip
              label="Față"
              size="small"
              sx={{
                position: "absolute",
                bottom: 8,
                left: 8,
                fontSize: "0.65rem",
                height: 20,
              }}
            />
          </Box>

          {/* Verso card */}
          {briefBack ? (
            <Box sx={{ ...fileCardStyle }}>
              <IconButton
                size="small"
                onClick={() => setBriefBack(null)}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  bgcolor: "rgba(0,0,0,0.15)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.3)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
              <ArticleIcon sx={{ fontSize: 36, color: "#FF6633" }} />
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                sx={{
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  px: 1,
                }}
              >
                {briefBack.name}
              </Typography>
              <Chip
                label="Verso"
                size="small"
                sx={{
                  position: "absolute",
                  bottom: 8,
                  left: 8,
                  fontSize: "0.65rem",
                  height: 20,
                }}
              />
            </Box>
          ) : (
            <Box
              role="button"
              tabIndex={0}
              aria-label={Vocabulary.addBackSide}
              sx={addVersoCardStyle(dragOver === "verso")}
              onClick={() => briefBackInputRef.current?.click()}
              onKeyDown={activateOnEnterOrSpace(() => briefBackInputRef.current?.click())}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver("verso");
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(null);
                handleBriefBackSelect(e.dataTransfer.files);
              }}
            >
              <AddIcon sx={{ fontSize: 32, color: "#FF6633" }} />
              <Typography variant="caption" fontWeight={600} color="#FF6633">
                {Vocabulary.addBackSide}
              </Typography>
              <input
                ref={briefBackInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleBriefBackSelect(e.target.files)}
                style={{ display: "none" }}
              />
            </Box>
          )}
        </Box>
      ) : (
        /* Photo preview */
        <Box display="flex" flexDirection="column" gap={1.5}>
          {photo.previewUrl && (
            <Box position="relative" display="flex" justifyContent="center">
              <img
                src={photo.previewUrl}
                alt="Vehicul"
                style={{
                  width: "min(40%, 100%)",
                  maxWidth: "100%",
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
              <IconButton
                onClick={removePhoto}
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {/* Detection result */}
      {detected && (currentVehicle?.brand_r || currentVehicle?.model_r) && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          p={1.5}
          borderRadius={1}
          mt={2}
          sx={{ bgcolor: "rgba(76, 175, 80, 0.1)" }}
        >
          <CheckIcon sx={{ color: "success.main", fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: "success.dark" }}>
            {Vocabulary.vehicleDetectedLabel}:{" "}
            {currentVehicle?.brand_r?.nume || ""}{" "}
            {currentVehicle?.model_r?.nume || ""}
          </Typography>
        </Box>
      )}

      <StepActions
        onNext={onNext}
        onPrevious={onPrevious}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        nextLabel={
          detected ? Vocabulary.continue : Vocabulary.continueWithoutPhoto
        }
      />
    </Box>
  );
};

export default VehiclePhotoStep;
